import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import API_BASE_URL from '../config';
import './ComponentOrdering.css';

const ComponentOrdering = () => {
  const [suggestedOrders, setSuggestedOrders] = useState({});
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLongTermModal, setShowLongTermModal] = useState(false);
  const [longTermData, setLongTermData] = useState({ components: [], weeks: [] });
  const [longTermLoading, setLongTermLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState('Momentum Forecast');

  // Fetch scenarios from component planning API
  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        console.log('Fetching scenarios from:', `${API_BASE_URL}/api/component-planning/scenarios`);
        const response = await axios.get(`${API_BASE_URL}/api/component-planning/scenarios`);
        console.log('Scenarios response:', response.data);
        setScenarios(response.data);
        
        // Set default scenario to "Momentum Forecast" if available, otherwise first scenario
        if (response.data.includes('Momentum Forecast')) {
          setSelectedScenario('Momentum Forecast');
        } else if (response.data.length > 0) {
          setSelectedScenario(response.data[0]);
        }
        
        setError('');
      } catch (error) {
        console.error('Error fetching scenarios:', error);
        setError(`Failed to load scenarios: ${error.message}`);
      }
    };
    fetchScenarios();
  }, []);

  // Fetch suggested component orders data
  const fetchSuggestedOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching suggested component orders for scenario:', selectedScenario);
      const response = await axios.get(`${API_BASE_URL}/api/suggested-component-orders`, {
        params: { scenario: selectedScenario }
      });
      const data = response.data;
      console.log('Suggested orders response:', data);
      
      setSuggestedOrders(data.supplier_groups || {});
      setWeeks(data.weeks || []);
      
    } catch (error) {
      console.error('Error fetching suggested component orders:', error);
      setError(`Failed to load suggested component orders: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedScenario]);

  // Fetch data when component mounts or scenario changes
  useEffect(() => {
    if (selectedScenario) {
      fetchSuggestedOrders();
    }
  }, [selectedScenario, fetchSuggestedOrders]);

  const formatWeek = (weekStr) => {
    // Convert YYYY-WW format to more readable format
    const [year, week] = weekStr.split('-');
    return `W${week} ${year}`;
  };

  const truncateComponentDescription = (description) => {
    if (!description) return '';
    return description.length > 80 ? description.substring(0, 80) + '...' : description;
  };

  const getSupplierTotalOrders = (components) => {
    return Math.round(components.reduce((total, component) => total + component.suggested_order_amount, 0));
  };

  // Fetch long-term ordering plan data
  const fetchLongTermOrderingPlan = async () => {
    setLongTermLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/long-term-component-orders`, {
        params: { scenario: selectedScenario }
      });
      setLongTermData(response.data);
    } catch (error) {
      console.error('Error fetching long-term ordering plan:', error);
      setError(`Failed to load long-term ordering plan: ${error.message}`);
    } finally {
      setLongTermLoading(false);
    }
  };

  // Open long-term ordering plan modal
  const openLongTermModal = () => {
    setShowLongTermModal(true);
    fetchLongTermOrderingPlan();
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text('Long-term Component Ordering Plan', 14, 20);
    
    // Prepare table data
    const tableData = longTermData.components.map(component => {
      const row = [component.component_id];
      longTermData.weeks.forEach(week => {
        const weekData = component.weekly_orders[week] || 0;
        row.push(weekData > 0 ? Math.round(weekData).toString() : '');
      });
      return row;
    });
    
    // Prepare table headers
    const headers = ['Component ID', ...longTermData.weeks.map(week => formatWeek(week))];
    
    // Add table
    doc.autoTable({
      head: [headers],
      body: tableData,
      startY: 30,
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255
      }
    });
    
    doc.save('long-term-component-ordering-plan.pdf');
  };

  // Export to CSV
  const exportToCSV = () => {
    // Prepare headers
    const headers = ['Component ID', ...longTermData.weeks.map(week => formatWeek(week))];
    
    // Prepare data rows
    const csvData = longTermData.components.map(component => {
      const row = [component.component_id];
      longTermData.weeks.forEach(week => {
        const weekData = component.weekly_orders[week] || 0;
        row.push(weekData > 0 ? Math.round(weekData).toString() : '');
      });
      return row;
    });
    
    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'long-term-component-ordering-plan.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="component-ordering-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Component Ordering Dashboard</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>View suggested component orders for the next 4 weeks</li>
              <li>Orders are grouped by supplier for easy planning</li>
              <li>Track current inventory levels and suggested order amounts</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {/* Scenario Selector */}
        <div className="scenario-selector-pane">
          <div className="scenario-selector">
            <label htmlFor="scenario-select">Select Scenario:</label>
            <select
              id="scenario-select"
              value={selectedScenario}
              onChange={(e) => setSelectedScenario(e.target.value)}
              disabled={loading}
            >
              {scenarios.map((scenario) => (
                <option key={scenario} value={scenario}>
                  {scenario}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="component-ordering-content">
          <div className="component-ordering-header">
            <h2>Suggested Upcoming Component Orders</h2>
            <div className="long-term-button-container">
              <button 
                className="long-term-button"
                onClick={openLongTermModal}
                title="View Long-term Ordering Plan"
              >
                Long-term Ordering Plan
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message" style={{ color: 'red', padding: '10px', marginBottom: '20px' }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {loading && (
            <div className="loading">
              <p>Loading suggested component orders...</p>
            </div>
          )}

          {!loading && Object.keys(suggestedOrders).length === 0 && !error && (
            <div className="no-data">
              <p>No suggested component orders found for the next 4 weeks.</p>
            </div>
          )}

          {!loading && Object.keys(suggestedOrders).length > 0 && (
            <div className="suggested-orders-container">
              {Object.entries(suggestedOrders).map(([supplier, components]) => (
                <div key={supplier} className="supplier-group">
                  <div className="supplier-header">
                    <h3>{supplier}</h3>
                    <div className="supplier-total">
                      Total Suggested Orders: {getSupplierTotalOrders(components).toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="supplier-table-container">
                    <table className="supplier-table">
                      <thead>
                        <tr>
                          <th>Component ID</th>
                          <th>Description</th>
                          <th>Supplier #</th>
                          <th>Current Inventory</th>
                          <th>Suggested Order Amount</th>
                          {weeks.map((week) => (
                            <th key={week}>{formatWeek(week)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {components.map((component) => (
                          <tr key={component.component_id}>
                            <td className="component-id-cell">
                              {component.component_id}
                            </td>
                            <td className="component-description-cell" title={component.component_description}>
                              {truncateComponentDescription(component.component_description)}
                            </td>
                            <td className="supplier-number-cell">
                              {component.supplier_number || '-'}
                            </td>
                            <td className="inventory-cell">
                              {component.inventory_count.toLocaleString()}
                            </td>
                            <td className="suggested-order-cell">
                              <strong>{Math.round(component.suggested_order_amount).toLocaleString()}</strong>
                            </td>
                            {weeks.map((week) => (
                              <td key={week} className="week-cell">
                                {component.weekly_orders[week] > 0 ? Math.round(component.weekly_orders[week]).toLocaleString() : '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Long-term Ordering Plan Modal */}
      {showLongTermModal && (
        <div className="modal-overlay" onClick={() => setShowLongTermModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Long-term Component Ordering Plan</h2>
              <button 
                className="modal-close-button"
                onClick={() => setShowLongTermModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {longTermLoading ? (
                <div className="loading">Loading long-term ordering plan...</div>
              ) : (
                <>
                  <div className="report-controls">
                    <button className="export-button pdf" onClick={exportToPDF}>
                      📄 Export PDF
                    </button>
                    <button className="export-button csv" onClick={exportToCSV}>
                      📊 Export CSV
                    </button>
                  </div>
                  
                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Component ID</th>
                          {longTermData.weeks.map((week, index) => (
                            <th key={week}>
                              <div className="week-header">
                                <div className="week-number">{formatWeek(week)}</div>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {longTermData.components.map((component, index) => (
                          <tr key={index}>
                            <td className="component-name-cell" title={component.component_description || component.component_id}>
                              {component.component_id}
                            </td>
                            {longTermData.weeks.map((week, weekIndex) => {
                              const weekData = component.weekly_orders[week] || 0;
                              return (
                                <td key={weekIndex} className={weekData > 0 ? 'has-order' : ''}>
                                  {weekData > 0 ? Math.round(weekData) : ''}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComponentOrdering; 